import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Import Types
import type { WhatsAppPlaceholderMapping, WhatsAppTemplateComponentApi, WhatsappTemplateApiResponse } from 'types';
// Import Services
import { getTemplateValidationTranslatableErrorText } from 'services/template';
// Import Hooks
import useFieldValidity from 'hooks/UseFieldValidity';
import useValidator from 'hooks/UseValidator';
// Import Enums
import { WhatsAppComponentTypeEnum, WhatsAppHeaderFormatEnum } from 'enums';
// Import Constants
import { l, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage } from 'constants/template';

type LanguageValidationPayload = Nullable<string>;

interface LanguageDetailsValidationPayload {
    status: Nullable<unknown>;
    category: Nullable<unknown>;
    subCategory: Nullable<unknown>;
    components: Nullable<WhatsAppTemplateComponentApi[]>;
}

interface PlaceholderValidationPayload {
    mapping: Nullable<WhatsAppPlaceholderMapping>;
    requiredPlaceholders: Array<{ componentKey: string; paramIndex: number }>;
}

interface UseWhatsAppTemplateValidationProps {
    templateLangKey: string;
    templateLanguageKeys: string[];
    allTemplateData: Record<string, AnyObject>;
    templateData: AnyObject;
    selectedTemplateName: Nullable<string>;
    selectedTemplateByLanguage: Nullable<WhatsappTemplateApiResponse>;
    templatesByLanguage: Nullable<WhatsappTemplateApiResponse[]>;
}

const getLanguageValidationPayload = (language: Nilable<string>): LanguageValidationPayload => language ?? null;

const getLanguageDetailsValidationPayload = (
    language: Nilable<string>,
    selectedTemplate?: Nullable<WhatsappTemplateApiResponse>,
): Nullable<LanguageDetailsValidationPayload> => {
    if (!language) {
        return null;
    }

    return {
        status: selectedTemplate?.Status ?? null,
        category: selectedTemplate?.Category ?? null,
        subCategory: selectedTemplate?.SubCategory ?? null,
        components: selectedTemplate?.Components ?? null,
    };
};

const getPlaceholderMappingValidationPayload = (
    selectedTemplate?: Nullable<WhatsappTemplateApiResponse>,
    mapping?: Nullable<WhatsAppPlaceholderMapping>,
): PlaceholderValidationPayload => {
    const requiredPlaceholders =
        selectedTemplate?.Components?.filter(
            (comp) =>
                (comp?.Example?.Params?.length ?? 0) > 0 &&
                !(comp.Type === WhatsAppComponentTypeEnum.Header && comp.Format === WhatsAppHeaderFormatEnum.Image) &&
                comp.Type !== WhatsAppComponentTypeEnum.Buttons,
        )?.flatMap((comp) =>
            (comp.Example?.Params ?? []).map((_, paramIndex) => ({
                componentKey: comp.Type ?? '',
                paramIndex,
            })),
        ) ?? [];

    return {
        mapping: mapping ?? null,
        requiredPlaceholders,
    };
};

const useWhatsAppTemplateValidation = (options: UseWhatsAppTemplateValidationProps) => {
    const {
        templateLangKey,
        templateLanguageKeys,
        allTemplateData,
        templateData,
        selectedTemplateName,
        selectedTemplateByLanguage,
        templatesByLanguage,
    } = options;

    const { t } = useTranslation();
    const isInitialMount = useRef(true);

    const languageValidationState = useFieldValidity(
        TemplateInputTypes.ExternalWhatsAppTemplateLanguage,
        l.Language,
        templateLangKey,
    );
    const languageDetailsValidationState = useFieldValidity(
        TemplateInputTypes.ExternalWhatsAppTemplateDetails,
        l.Language,
        templateLangKey,
    );
    const templateValidationState = useFieldValidity(
        TemplateInputTypes.ExternalWhatsAppTemplateId,
        l.Template,
        DefaultLanguage,
    );
    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));

    useEffect(
        () => {
            if (isInitialMount.current) return;

            validator({
                [templateLangKey + TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping]:
                    getPlaceholderMappingValidationPayload(
                        selectedTemplateByLanguage,
                        templateData?.[TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping],
                    ),
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            templateData?.[TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping],
            selectedTemplateByLanguage,
            templateLangKey,
        ],
    );

    useEffect(() => {
        if (isInitialMount.current || !selectedTemplateName || !templatesByLanguage?.length) return;

        const allTabsPayload = templateLanguageKeys.reduce<Record<string, unknown>>((acc, langKey) => {
            const langData = allTemplateData[langKey];
            const selectedTemplate = (templatesByLanguage ?? []).find(
                (template) => template?.Language === langData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage],
            );

            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplateLanguage] = getLanguageValidationPayload(
                langData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage] as Nilable<string>,
            );
            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplateDetails] = getLanguageDetailsValidationPayload(
                langData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage] as Nilable<string>,
                selectedTemplate,
            );
            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping] =
                getPlaceholderMappingValidationPayload(
                    selectedTemplate,
                    langData?.[
                        TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping
                    ] as Nullable<WhatsAppPlaceholderMapping>,
                );

            return acc;
        }, {});

        validator(allTabsPayload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplateName, templatesByLanguage, allTemplateData, templateLanguageKeys]);

    useEffect(
        () => {
            if (isInitialMount.current || !selectedTemplateName) return;

            validator({
                [templateLangKey + TemplateInputTypes.ExternalWhatsAppTemplateLanguage]: getLanguageValidationPayload(
                    templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage] as Nilable<string>,
                ),
                [templateLangKey + TemplateInputTypes.ExternalWhatsAppTemplateDetails]:
                    getLanguageDetailsValidationPayload(
                        templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage] as Nilable<string>,
                        selectedTemplateByLanguage,
                    ),
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage],
            selectedTemplateName,
            selectedTemplateByLanguage,
        ],
    );

    useEffect(
        () => {
            if (isInitialMount.current) {
                isInitialMount.current = false;
                return;
            }

            validator({
                [templateLangKey + TemplateInputTypes.ExternalWhatsAppTemplateId]:
                    templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateId],
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedTemplateName],
    );

    return {
        validator,
        languageValidationState,
        languageDetailsValidationState,
        templateValidationState,
        getLanguageValidationPayload,
        getLanguageDetailsValidationPayload,
        getPlaceholderMappingValidationPayload,
    };
};

export default useWhatsAppTemplateValidation;
